import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useDispatch } from "react-redux";
import PageLayout from "../../components/PageLayout";
import AxiosInstance from "../../services/networkServices";
import { getEngagementStats } from "../../http/analytics";
import { getInProgressMatches } from "../../http/inProgressMatches";
import { showToast } from "../../store/slices/common";
import {
  FOR_FUN_LABEL,
  NO_RANK_LABEL,
  RANK_COLORS,
  formatRankLabel,
} from "../InProgressMatches/matchDisplay";
import {
  FIRST_TEN_COUNT,
  PREVIEW_COUNT_MAX,
  PREVIEW_COUNT_MIN,
  RANK_ORDER,
  firstTenRanks,
  rankWeightsDiffer,
  readPreviewCount,
  readRankWeights,
} from "./firstTen";
import MinSupportedBuildCard from "./MinSupportedBuildCard";

dayjs.extend(utc);
dayjs.extend(timezone);

const EST_TZ = "America/New_York";

const RANK_LABELS = {
  UNRANKED: "Unranked",
  IRON: "Iron",
  BRONZE: "Bronze",
  SILVER: "Silver",
  GOLD: "Gold",
  PLATINUM: "Platinum",
  DIAMOND: "Diamond",
  LEGEND: "Legend",
  [FOR_FUN_LABEL]: formatRankLabel(FOR_FUN_LABEL),
  [NO_RANK_LABEL]: "Unknown",
};

const cardSx = {
  padding: { xs: "14px", sm: "20px" },
  borderRadius: "10px",
  boxShadow: "rgba(0, 0, 0, 0.05) 0 6px 24px 0, rgba(0, 0, 0, 0.08) 0 0 0 1px",
  height: "100%",
  boxSizing: "border-box",
};

const sectionTitleSx = {
  fontWeight: 700,
  mb: 0.5,
  fontSize: { xs: "1.05rem", sm: "1.25rem" },
};

/** 16px type so iOS doesn't zoom the page when a field is focused. */
const numberFieldSx = {
  width: { xs: 168, sm: 220 },
  flexShrink: 0,
  "& .MuiInputBase-input": {
    fontSize: 16,
    textAlign: "right",
    py: { xs: "12px", sm: "8.5px" },
  },
};

const toInputMap = (map) =>
  Object.fromEntries(RANK_ORDER.map((rank) => [rank, String(map?.[rank] ?? "")]));

/** Whole numbers of at least 1, or null when any rank is blank or invalid. */
const readVoteThresholds = (inputMap) => {
  const thresholds = {};
  for (const rank of RANK_ORDER) {
    const raw = String(inputMap?.[rank] ?? "").trim();
    const value = Number(raw);
    if (raw === "" || !Number.isInteger(value) || value < 1) return null;
    thresholds[rank] = value;
  }
  return thresholds;
};

const rawInputsDiffer = (saved, draft) =>
  RANK_ORDER.some(
    (rank) => String(draft?.[rank] ?? "").trim() !== String(saved?.[rank] ?? "")
  );

/** What a threshold save did to in-progress matches. Empty when none changed. */
const describeThresholdSave = (applied) => {
  const changed = applied?.voteThresholds?.changed || {};
  const ranks = Object.keys(changed);
  if (!ranks.length) return "";
  const total = applied.voteThresholds.restampedTotal || 0;
  const names = ranks.map((rank) => RANK_LABELS[rank] || rank).join(", ");
  const ending = applied.voteThresholds.sweepStarted
    ? "; matches already at or over it are ending now"
    : "";
  return ` ${total} in-progress ${
    total === 1 ? "match" : "matches"
  } moved to the new ${names} vote ${ranks.length === 1 ? "threshold" : "thresholds"}${ending}.`;
};

const formatCount = (value) =>
  value === null || value === undefined ? "—" : (Number(value) || 0).toLocaleString();

const sameSequence = (a, b) =>
  a.length === b.length && a.every((rank, index) => rank === b[index]);

const RankSequence = ({ ranks, emptyLabel }) => {
  if (!ranks.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: { xs: "8px", sm: "6px" },
      }}
    >
      {ranks.map((rank, index) => (
        <React.Fragment key={`${rank}-${index}`}>
          {index > 0 && (
            <Typography
              component="span"
              sx={{ color: "#90a4ae", fontWeight: 700, lineHeight: 1 }}
            >
              -
            </Typography>
          )}
          <Box
            component="span"
            sx={{
              backgroundColor: RANK_COLORS[rank] || RANK_COLORS[NO_RANK_LABEL],
              color: "#fff",
              fontWeight: 700,
              fontSize: { xs: "13px", sm: "12px" },
              letterSpacing: "0.04em",
              borderRadius: "6px",
              padding: { xs: "6px 10px", sm: "4px 8px" },
              lineHeight: 1.4,
            }}
          >
            {formatRankLabel(rank)}
          </Box>
        </React.Fragment>
      ))}
    </Box>
  );
};

const RankField = ({ label, children }) => (
  <Box
    component="label"
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1.5,
      py: { xs: 1.25, sm: 0.75 },
      borderBottom: "1px solid #eee",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography
      component="span"
      sx={{
        flex: "1 1 auto",
        minWidth: 0,
        fontWeight: 600,
        fontSize: { xs: 16, sm: 14 },
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const StatCard = ({ label, value, hint }) => (
  <Paper sx={cardSx}>
    <Typography sx={{ fontSize: "13px", color: "text.secondary", fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: { xs: "28px", md: "34px" }, fontWeight: 700, color: "#142C5C", lineHeight: 1.2 }}
    >
      {value}
    </Typography>
    {hint && (
      <Typography sx={{ fontSize: "12px", color: "text.secondary", mt: 0.5 }}>{hint}</Typography>
    )}
  </Paper>
);

const MobilePage = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newUsers, setNewUsers] = useState(null);
  const [dau, setDau] = useState(null);
  const [summary, setSummary] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [matchesLoaded, setMatchesLoaded] = useState(false);
  const [factors, setFactors] = useState(null);
  const [savedWeights, setSavedWeights] = useState(null);
  const [draftWeights, setDraftWeights] = useState(toInputMap({}));
  const [savedThresholds, setSavedThresholds] = useState(null);
  const [draftThresholds, setDraftThresholds] = useState(toInputMap({}));
  const [previewCountInput, setPreviewCountInput] = useState(String(FIRST_TEN_COUNT));
  const [openingCount, setOpeningCount] = useState(FIRST_TEN_COUNT);
  const savedWeightsRef = useRef(null);
  const draftWeightsRef = useRef(draftWeights);
  const savedThresholdsRef = useRef(null);
  const draftThresholdsRef = useRef(draftThresholds);
  const postSaveRefreshTimer = useRef(null);
  savedWeightsRef.current = savedWeights;
  draftWeightsRef.current = draftWeights;
  savedThresholdsRef.current = savedThresholds;
  draftThresholdsRef.current = draftThresholds;

  const load = useCallback(async ({ replaceDrafts = false } = {}) => {
    const today = dayjs().tz(EST_TZ).format("YYYY-MM-DD");
    setLoading(true);
    try {
      const [engagementResult, matchesResult, gameplayResult] = await Promise.all([
        getEngagementStats({ timezone: "EST", startDate: today, endDate: today }).catch(
          (error) => {
            console.error("Failed to load today's engagement:", error);
            return null;
          }
        ),
        getInProgressMatches({
          page: 1,
          limit: 1,
          includeWeightBuckets: true,
        }).catch((error) => {
          console.error("Failed to load in-progress matches:", error);
          return null;
        }),
        AxiosInstance.get("/admin/settings/gameplay").catch((error) => {
          console.error("Failed to load gameplay settings:", error);
          return null;
        }),
      ]);

      if (engagementResult?.data) {
        const todayRow = (engagementResult.data.daily || []).find((row) => row.date === today);
        setNewUsers(todayRow?.signups || 0);
        setDau(todayRow?.activeUsers || 0);
      } else {
        setNewUsers(null);
        setDau(null);
      }

      if (matchesResult?.data?.summary) {
        setSummary(matchesResult.data.summary);
        setBuckets(matchesResult.data.weightBuckets || []);
        setMatchesLoaded(true);
      } else {
        setSummary(null);
        setBuckets([]);
        setMatchesLoaded(false);
      }

      const gameplay = gameplayResult?.data?.data?.gameplay;
      if (gameplay) {
        const nextWeights = gameplay.fullEnergyRankWeights || {};
        const nextThresholds = gameplay.voteThresholds || {};
        const weightsUntouched =
          replaceDrafts ||
          !savedWeightsRef.current ||
          !rawInputsDiffer(savedWeightsRef.current, draftWeightsRef.current);
        const thresholdsUntouched =
          replaceDrafts ||
          !savedThresholdsRef.current ||
          !rawInputsDiffer(savedThresholdsRef.current, draftThresholdsRef.current);
        setFactors({
          categoryRankWeights: gameplay.categoryRankWeights || {},
          categoryFeedWeights: gameplay.categoryFeedWeights || {},
          groupDuetFeedWeight: gameplay.groupDuetFeedWeight,
          oldMatchFeedBoost: gameplay.oldMatchFeedBoost,
          consolidated: Boolean(gameplay.consolidatedFeed?.enabled),
          energyMax: gameplay.energyMax,
          liveMatchFeedWeight: gameplay.liveMatchFeedWeight,
          forFunWeight: gameplay.forFunFeedWeights?.fullEnergy ?? 1,
        });
        setSavedWeights(nextWeights);
        setSavedThresholds(nextThresholds);
        if (weightsUntouched) setDraftWeights(toInputMap(nextWeights));
        if (thresholdsUntouched) setDraftThresholds(toInputMap(nextThresholds));
      }

      if (!engagementResult || !matchesResult || !gameplay) {
        dispatch(
          showToast({
            color: "error",
            msg: "Some Mobile stats could not be loaded.",
          })
        );
      }
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
    return () => clearTimeout(postSaveRefreshTimer.current);
  }, [load]);

  const byRankMap = new Map((summary?.byRank || []).map((row) => [row.rank, row.count]));
  // Every tier plus For fun always shows; UNKNOWN only when present.
  const rankRows = [...RANK_ORDER, FOR_FUN_LABEL, NO_RANK_LABEL]
    .filter((rank) => rank !== NO_RANK_LABEL || byRankMap.has(NO_RANK_LABEL))
    .map((rank) => ({ rank, count: byRankMap.get(rank) || 0 }));

  const previewInput = useMemo(
    () => ({
      buckets,
      categoryRankWeights: factors?.categoryRankWeights || {},
      categoryFeedWeights: factors?.categoryFeedWeights || {},
      groupDuetFeedWeight: factors?.groupDuetFeedWeight,
      oldMatchFeedBoost: factors?.oldMatchFeedBoost,
      forFunWeight: factors?.forFunWeight,
    }),
    [buckets, factors]
  );

  const savedSequence = useMemo(
    () =>
      savedWeights
        ? firstTenRanks({ ...previewInput, rankWeights: savedWeights, count: openingCount })
        : [],
    [previewInput, savedWeights, openingCount]
  );

  const draftParsed = useMemo(() => readRankWeights(draftWeights), [draftWeights]);
  const draftThresholdsParsed = useMemo(
    () => readVoteThresholds(draftThresholds),
    [draftThresholds]
  );
  const weightsDirty =
    Boolean(savedWeights && draftParsed) && rankWeightsDiffer(savedWeights, draftParsed);
  const thresholdsDirty =
    Boolean(savedThresholds && draftThresholdsParsed) &&
    RANK_ORDER.some(
      (rank) => Number(savedThresholds[rank]) !== draftThresholdsParsed[rank]
    );
  const draftSequence = useMemo(
    () =>
      weightsDirty
        ? firstTenRanks({ ...previewInput, rankWeights: draftParsed, count: openingCount })
        : [],
    [previewInput, draftParsed, weightsDirty, openingCount]
  );

  const previewCountInvalid = readPreviewCount(previewCountInput) == null;

  const onPreviewCountChange = (event) => {
    const next = event.target.value;
    setPreviewCountInput(next);
    const parsed = readPreviewCount(next);
    if (parsed != null) setOpeningCount(parsed);
  };

  const handleSave = async () => {
    if (!draftParsed) {
      dispatch(
        showToast({
          color: "error",
          msg: "Each post-energy weight must be a number greater than 0.",
        })
      );
      return;
    }
    if (!draftThresholdsParsed) {
      dispatch(
        showToast({
          color: "error",
          msg: "Each vote threshold must be a whole number of at least 1.",
        })
      );
      return;
    }
    if (!factors) return;

    try {
      setSaving(true);
      const response = await AxiosInstance.put("/admin/settings/gameplay", {
        energyMax: factors.energyMax,
        voteThresholds: draftThresholdsParsed,
        fullEnergyRankWeights: draftParsed,
        groupDuetFeedWeight: factors.groupDuetFeedWeight,
        liveMatchFeedWeight: factors.liveMatchFeedWeight,
      });
      if (response.data.status) {
        const gameplay = response.data.data?.gameplay;
        const nextWeights = gameplay?.fullEnergyRankWeights || draftParsed;
        const nextThresholds = gameplay?.voteThresholds || draftThresholdsParsed;
        setSavedWeights(nextWeights);
        setDraftWeights(toInputMap(nextWeights));
        setSavedThresholds(nextThresholds);
        setDraftThresholds(toInputMap(nextThresholds));
        const savedBits = [
          weightsDirty ? "post-energy feed weights" : null,
          thresholdsDirty ? "vote thresholds" : null,
        ].filter(Boolean);
        const lead = savedBits.join(" and ") || "settings";
        dispatch(
          showToast({
            color: "success",
            msg: `${lead.charAt(0).toUpperCase()}${lead.slice(1)} saved.${describeThresholdSave(
              response.data.data?.applied
            )}`,
          })
        );
        if (thresholdsDirty) {
          clearTimeout(postSaveRefreshTimer.current);
          load({ replaceDrafts: true });
          if (response.data.data?.applied?.voteThresholds?.sweepStarted) {
            postSaveRefreshTimer.current = setTimeout(
              () => load({ replaceDrafts: true }),
              6000
            );
          }
        }
      } else {
        dispatch(
          showToast({
            color: "error",
            msg: response.data.message || "Failed to save Mobile settings.",
          })
        );
      }
    } catch (error) {
      console.error("Error saving Mobile settings:", error);
      dispatch(
        showToast({
          color: "error",
          msg: error?.response?.data?.message || "Failed to save Mobile settings.",
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout
      title="Mobile"
      subtitle={`Today's new accounts and active users, in-progress pagents by rank, the post-energy feed's first ${openingCount}, and per-rank vote thresholds.`}
      loading={loading}
      sx={{ pb: { xs: "96px", md: 0 } }}
      actions={
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={load}
          disabled={loading || saving}
          sx={{
            textTransform: "none",
            width: { xs: "100%", sm: "auto" },
            minHeight: { xs: 44, sm: 36 },
          }}
        >
          Refresh
        </Button>
      }
    >
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid item xs={12}>
          <MinSupportedBuildCard />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard
            label="New users today"
            value={formatCount(newUsers)}
            hint="Accounts registered today (EST)"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <StatCard
            label="DAU"
            value={formatCount(dau)}
            hint="Distinct accounts active today (EST)"
          />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={cardSx}>
            <Typography variant="h6" sx={sectionTitleSx}>
              In progress by rank
              {summary ? ` (${formatCount(summary.total)} live)` : ""}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Every in-progress pagent right now, including live battles. Curated
              copies are excluded.
            </Typography>
            {!summary && !loading ? (
              <Typography variant="body2" color="text.secondary">
                In-progress counts could not be loaded.
              </Typography>
            ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(4, minmax(0, 1fr))",
                },
                gap: { xs: "8px", sm: "12px" },
              }}
            >
              {rankRows.map((row) => (
                <Box
                  key={row.rank}
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "12px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 700,
                      color: RANK_COLORS[row.rank] || "#142C5C",
                    }}
                  >
                    {RANK_LABELS[row.rank] || row.rank}
                  </Typography>
                  <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#142C5C" }}>
                    {formatCount(row.count)}
                  </Typography>
                </Box>
              ))}
            </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={cardSx}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography variant="h6" sx={{ ...sectionTitleSx, mb: 0 }}>
                First
              </Typography>
              <TextField
                size="small"
                type="number"
                value={previewCountInput}
                onChange={onPreviewCountChange}
                error={previewCountInvalid}
                inputProps={{
                  min: PREVIEW_COUNT_MIN,
                  max: PREVIEW_COUNT_MAX,
                  step: 1,
                  inputMode: "numeric",
                  autoComplete: "off",
                  "aria-label": "Opening pagents to preview",
                }}
                sx={{ ...numberFieldSx, width: { xs: 88, sm: 96 } }}
              />
              <Typography variant="h6" sx={{ ...sectionTitleSx, mb: 0 }}>
                chance
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {openingCount === 1
                ? "A typical first in-progress pagent a full-bar viewer or guest would see."
                : `A typical first ${openingCount} in-progress pagents a full-bar viewer or guest would see.`}{" "}
              Contests lead. Everything else follows rank weight × category
              weight × group boost × old-pagent rate per 15 minutes × random, so a high
              weight and a lot of pagents at that rank show up together. Live
              battles are mixed in separately and are not in this line. Set the
              number from {PREVIEW_COUNT_MIN} to {PREVIEW_COUNT_MAX}.
              {factors?.consolidated
                ? " Feed consolidation is on, so the app is serving the consolidated weights right now. This line is the post-energy feed, which applies when consolidation is off."
                : ""}
            </Typography>
            {previewCountInvalid && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                Use a whole number from {PREVIEW_COUNT_MIN} to {PREVIEW_COUNT_MAX}. This
                line is still showing {openingCount}.
              </Typography>
            )}
            <RankSequence
              ranks={savedSequence}
              emptyLabel={
                matchesLoaded
                  ? "No in-progress pagents are in the post-energy draw."
                  : `The first ${openingCount} could not be loaded.`
              }
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={cardSx}>
            <Typography variant="h6" sx={sectionTitleSx}>
              Post-energy feed weights
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Weight per rank for viewers with a full energy bar, and for guests.
              Higher weights surface that rank more often. Decimals are allowed.
              Category, group, and old-pagent rates stay as they are on Match
              & Energy and still count in the first {openingCount} above.
            </Typography>
            <Box>
              {RANK_ORDER.map((rank) => (
                <RankField key={rank} label={RANK_LABELS[rank]}>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{
                      min: 0,
                      step: 0.5,
                      inputMode: "decimal",
                      autoComplete: "off",
                      "aria-label": `${RANK_LABELS[rank]} post-energy weight`,
                    }}
                    value={draftWeights[rank] ?? ""}
                    onChange={(event) =>
                      setDraftWeights((prev) => ({
                        ...prev,
                        [rank]: event.target.value,
                      }))
                    }
                    error={
                      String(draftWeights[rank] ?? "").trim() !== "" &&
                      !(Number(draftWeights[rank]) > 0)
                    }
                    sx={numberFieldSx}
                  />
                </RankField>
              ))}
            </Box>

            {weightsDirty && (
              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: "8px",
                  border: "1px solid #142C5C",
                  backgroundColor: "#f7f9fc",
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 1, color: "#142C5C" }}>
                  If you save
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {sameSequence(savedSequence, draftSequence)
                    ? `A typical first ${openingCount} would stay the same.`
                    : `A typical first ${openingCount} would look like this.`}
                </Typography>
                <RankSequence
                  ranks={draftSequence}
                  emptyLabel="No in-progress pagents are in the post-energy draw."
                />
              </Box>
            )}

            {!draftParsed && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Each weight must be a number greater than 0.
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={cardSx}>
            <Typography variant="h6" sx={sectionTitleSx}>
              Vote thresholds
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              A match ends when one side reaches its rank&apos;s first to X
              votes. Saving applies a changed number to every in-progress match
              of that rank: their First to X moves to the new number, and any
              match already at or over a lowered target ends right away.
              Categories with their own table on Match &amp; Energy keep that
              table.
            </Typography>
            <Box>
              {RANK_ORDER.map((rank) => {
                const raw = String(draftThresholds[rank] ?? "").trim();
                const value = Number(raw);
                const invalid = raw !== "" && (!Number.isInteger(value) || value < 1);
                return (
                  <RankField key={rank} label={RANK_LABELS[rank]}>
                    <TextField
                      size="small"
                      type="number"
                      inputProps={{
                        min: 1,
                        step: 1,
                        inputMode: "numeric",
                        autoComplete: "off",
                        "aria-label": `${RANK_LABELS[rank]} vote threshold`,
                      }}
                      value={draftThresholds[rank] ?? ""}
                      onChange={(event) =>
                        setDraftThresholds((prev) => ({
                          ...prev,
                          [rank]: event.target.value,
                        }))
                      }
                      error={invalid}
                      sx={numberFieldSx}
                    />
                  </RankField>
                );
              })}
            </Box>

            {!draftThresholdsParsed && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Each vote threshold must be a whole number of at least 1.
              </Typography>
            )}

            <Box
              sx={{
                position: { xs: "fixed", md: "static" },
                left: { xs: 0, md: "auto" },
                right: { xs: 0, md: "auto" },
                bottom: { xs: 0, md: "auto" },
                zIndex: { xs: 10, md: "auto" },
                mt: { xs: 0, md: 2 },
                px: { xs: 1.5, md: 0 },
                pt: { xs: 1.5, md: 0 },
                pb: { xs: "calc(12px + env(safe-area-inset-bottom))", md: 0 },
                bgcolor: { xs: "#fff", md: "transparent" },
                borderTop: { xs: "1px solid #e6e6e6", md: "none" },
                boxShadow: { xs: "0 -6px 20px rgba(20, 44, 92, 0.08)", md: "none" },
              }}
            >
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={
                  loading ||
                  saving ||
                  !draftParsed ||
                  !draftThresholdsParsed ||
                  (!weightsDirty && !thresholdsDirty)
                }
                sx={{
                  textTransform: "none",
                  width: { xs: "100%", md: "auto" },
                  minHeight: { xs: 48, md: 36 },
                  fontSize: { xs: 16, md: 14 },
                }}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </PageLayout>
  );
};

export default MobilePage;
